// hooks.ts
import { useCallback, useEffect, useRef, useState } from 'react';

type UseControlledConfig<Value, OnChangeType> = {
  controlledValue?: Value;
  initialValue: Value;
  name: string;
  onChange?: OnChangeType;
};

export type Updater<Value> = Value | ((prevValue: Value) => Value);

// Extracts the params that follow the value from an onChange callback, so the
// setter can forward them through to onChange.
type AdditionalParams<OnChangeType> = OnChangeType extends (
  newValue: any,
  ...additionalParams: infer Rest
) => void
  ? Rest
  : [];

/**
 * A custom hook that handles the logic for managing controlled and uncontrolled components.
 *
 * @template Value - The type of the value being controlled.
 * @param {object} config - An object containing the configuration options for the hook.
 * @param {Value | undefined} config.controlledValue - The controlled value to use. If this is defined, the component is controlled.
 * @param {Value} config.initialValue - The initial value to use for the component. This is only used if the component is uncontrolled.
 * @param {string} config.name - The name of the component. Used for warning messages.
 * @param {(newValue: Value, ...additionalParams: Array<any>) => void | undefined} config.onChange - An optional callback that is called when the controlled value changes. May declare additional params after the value.
 * @returns {[Value, (updater: Updater<Value>, ...additionalParams) => void]} - A tuple containing the current value and a setter. The setter accepts either a new value or a function receiving the previous value and returning the new one, followed by any additional params that are forwarded to onChange.
 */
export const useControlled = <
  Value,
  OnChangeType extends (newValue: Value, ...additionalParams: Array<any>) => void = (
    newValue: Value,
    ...additionalParams: Array<any>
  ) => void,
>({
  controlledValue,
  initialValue,
  name,
  onChange,
}: UseControlledConfig<Value, OnChangeType>): readonly [
  Value,
  (updater: Updater<Value>, ...additionalParams: AdditionalParams<OnChangeType>) => void,
] => {
  const isControlled = onChange !== undefined;
  const { current: origIsControlled } = useRef(isControlled);
  const [internalValue, setInternalValue] = useState(controlledValue ?? initialValue);
  const { current: origInitialValue } = useRef(initialValue);
  const value = (origIsControlled ? (controlledValue ?? internalValue) : internalValue) as Value;

  const valueRef = useRef(value);
  valueRef.current = value;
  const controlledValueRef = useRef(controlledValue);
  controlledValueRef.current = controlledValue;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (origIsControlled !== isControlled) {
        console.warn(
          `"${name}" is changed from ${
            origIsControlled ? 'uncontrolled to controlled' : 'controlled to uncontrolled'
          }.`,
        );
      }
    }
  }, [origIsControlled, isControlled, name]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (!origIsControlled && origInitialValue !== initialValue) {
        console.warn(
          `"${name}" is changing its defaultValue after being initialized. Make "${name}" a controlled component instead.`,
        );
      }
    }
  }, [origInitialValue, initialValue, name, origIsControlled]);

  const setValueIfUncontrolled = useCallback(
    (updater: Updater<Value>, ...additionalParams: AdditionalParams<OnChangeType>) => {
      if (!origIsControlled) {
        setInternalValue(updater as Value | ((prev: Value) => Value));
      } else {
        const resolvedValue =
          typeof updater === 'function'
            ? (updater as (prevValue: Value) => Value)(valueRef.current)
            : updater;
        valueRef.current = resolvedValue;
        onChange?.(resolvedValue, ...additionalParams);
        if (controlledValueRef.current === undefined) {
          setInternalValue(resolvedValue);
        }
      }
    },
    [origIsControlled, onChange],
  );

  useEffect(() => {
    if (isControlled || controlledValue === undefined) return;
    setInternalValue(controlledValue);
  }, [controlledValue]);

  return [value, setValueIfUncontrolled] as const;
};
